import { Listbox, Popover, Transition } from "@headlessui/react"
import {
	CheckIcon,
	ChevronDownIcon,
	ChevronRightIcon,
	InformationCircleIcon
} from "@heroicons/react/24/outline"
import { DevTool } from "@hookform/devtools"
import { zodResolver } from "@hookform/resolvers/zod";
import React, { Fragment, useEffect, useRef, useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useContentContext } from "../utils/ContentProvider"
import { classNames } from "../utils/helpers"
import { allowedParams, getAnnualMileageOptions, getVehicleTypeOptions, } from "../utils/options"
import { doInitialQuery, sendEvent } from "../utils/queries"
import { getRuntimeHostname, getRuntimeReferrer, isVertaaensinHost } from "../utils/runtimeOverrides"
import { SignUpSchema, SignUpSchemaType } from "../utils/types"
import NotFound from "./NotFound"
import NotificationModal from "./NotificationModal"

const isProd: boolean =
	(document.baseURI.indexOf("wl.vakuutuslaskuri") > -1)

const DetailsForm = ({ setInsuranceType }: any) => {
	const navigate = useNavigate()
	const params = useParams()
	const uselocation = useLocation()
	const [searchParams] = useSearchParams();
	const referrer = getRuntimeReferrer()
	const referrerLower = referrer.toLowerCase()
	const referrerIsVakuutuslaskuri: boolean = (referrerLower.indexOf("vakuutustenvertailu.fi") > -1) ||
		(referrerLower.indexOf("vakuutusvertailu.fi") > -1)
	const referrerIsEffortia: boolean = (referrerLower.indexOf("vakuutustenvertailu") > -1) ||
		(referrerLower.indexOf("vakuutusvertailu") > -1) ||
		(referrerLower.indexOf("effortia") > -1)
	const { translations } = useContentContext()
	const [selectedAnnualMileage, setSelectedAnnualMileage] = useState<any>(getAnnualMileageOptions(translations.detailsForm.annualMileageTitles)[0])
	const cancelButtonRef = useRef(null)
	const [open, setOpen] = useState(false)
	const [showProfessionalUseWarning, setShowProfessionalUseWarning] = useState(false)
	const [showMainUserWarning, setShowMainUserWarning] = useState(false)
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const hostname = getRuntimeHostname()
	const hideHeader: boolean = (referrerLower.indexOf("vakuutustenvertailu.fi") > -1) ||
		(referrerLower.indexOf("vakuutusvertailu.fi") > -1) ||
		(referrerLower.indexOf("vertaaensin.fi") > -1)
	const isVakuutustenvertailuHost: boolean = (hostname.indexOf("vakuutustenvertailu") > -1) ||
		(hostname.indexOf("vakuutusvertailu") > -1) ||
		(hostname.indexOf("effortia") > -1)
	const isVertaaensinLogoHost: boolean = hostname.indexOf("vertaaensin") > -1
	const logoSrc = isVertaaensinLogoHost
		? "/vertaaensin-logo-white.svg"
		: (isVakuutustenvertailuHost ? "/Vakuutustenvertailu-logo-white.png" : "/Vakuutuslaskuri-logo-white.png")
	const isVertaaensinTheme = isVertaaensinHost() || (referrerLower.indexOf("vertaaensin") > -1)
	const primaryCtaClass = isVertaaensinTheme
		? "bg-[#f58423] text-white hover:bg-[#f58423]"
		: "bg-[#0F6CBD] text-white hover:bg-[#0c589a]"
	const {
		register,
		setValue,
		handleSubmit,
		getValues,
		control,
		getFieldState,
		formState: { errors, isSubmitting, isValid, isDirty }
	} = useForm<SignUpSchemaType>({
		mode: "onChange",
		resolver: zodResolver(SignUpSchema)
	})

	const handleShowWarningModal = () => {
		setOpen(true)
	}

	const setInitialValuesFromCache = () => {
		if (uselocation.state && uselocation.state.vehicleType)
			setValue("vehicleType", uselocation.state.vehicleType)

		if (uselocation.state && uselocation.state.plateNumber)
			setValue("plateNumber", uselocation.state.plateNumber)

		if (uselocation.state && uselocation.state.postalNumber)
			setValue("postalNumber", uselocation.state.postalNumber)
	}

	const setInitialValuesForEffortia = () => {
		if (referrerIsEffortia) {
			setValue("vehicleType", "1", {
				shouldValidate: true,
				shouldTouch: true,
				shouldDirty: true
			})
			setValue("under25Driver", "true", {
				shouldValidate: true,
				shouldTouch: true,
				shouldDirty: true
			})
			setValue("mainUser", "true", {
				shouldValidate: true,
				shouldTouch: true,
				shouldDirty: true
			})
			setValue("professionalUse", "false", {
				shouldValidate: true,
				shouldTouch: true,
				shouldDirty: true
			})
		}
	}

		useEffect(() => {
			setInsuranceType(params.insuranceType ?? "invalid")

		if (uselocation && uselocation.state) {
			setInitialValuesFromCache()
			} else {
				setInitialValuesForEffortia()
			}
		}, [params.insuranceType, setInsuranceType, location])


	const onSubmit: SubmitHandler<SignUpSchemaType> = async (data) => {
		setIsLoading(true)

		const requestData: any = {
			productLineId: 1,
			identityNo: data.socialSecurityNumber,
			postCode: data.postalNumber,
			vehicleQuoteData: {
				vrm: data.plateNumber,
				annualMileage: data.annualMileage,
				ownerIsFinanceCompany: data.carFinanced === "true" ? true : false,
				financeCompanyId: 1, // No field for this in the form
				youngestDriverAge: data.under25Driver === "true" ? 24 : 25,
				alarmFitted: data.mainUser === "true" ? true : false, // No field for this in the form
				vehicleModified: data.professionalUse === "true" ? true : false, // No field for this in the form
			}
		}

		const initialQueryResponse = await doInitialQuery(requestData, isProd, referrer)

		if (!initialQueryResponse.authToken) {
			return alert("Error in initial query")
		}

		navigate({
			pathname: "/results",
			search: uselocation.search,
		}, {
			state: {
				...getValues(),
				token: initialQueryResponse.authToken,
				gclid: searchParams.get("gclid") ?? ""
			}
		})
	}

	if (!allowedParams.includes(params.insuranceType ?? "invalid")) {
		return <NotFound />
	}

	const formIsValid = (): boolean => {
		return isValid && getValues("mainUser") === "true" && getValues("professionalUse") === "false"
	}

	return (
		<div className={isVertaaensinTheme ? "vertaaensin-theme" : ""}>
			{!hideHeader &&
				<div className="flex flex-row justify-center py-4 bg-[#2396D2]">
					<img
						src={logoSrc}
						className="w-full max-w-[220px]" />
				</div>
			}
			<div className="bg-[#FAFAFA] sm:py-14">
				<div className="max-w-prose mx-auto border border-gray-300 rounded-lg bg-white shadow-md">
					<div className="m-auto p-10 text-center">
						<h2
							className="text-3xl font-medium text-gray-700">{translations.detailsForm.mainTitle}</h2>
					</div>
					<form
						className="flex flex-row flex-wrap container max-w-xl m-auto px-4 sm:px-6 lg:px-8 sm:mb-0"
						onSubmit={handleSubmit(onSubmit)}>
						<div className="flex flex-col w-full">
							<h3
								className="mb-2 pb-2 text-2xl font-medium text-gray-700">{translations.detailsForm.section1Header}</h3>
							<div className="flex flex-row flex-wrap">
								<div className="basis-full sm:basis-1/1 sm:pr-4 py-4 space-y-6">
									<fieldset>
										<legend
											className="flex flex-row items-center text-sm font-semibold leading-6 text-gray-900">
											{translations.detailsForm.vehicleTypeTitle}
											<Popover className="relative ml-2">
												<Popover.Button>
													<InformationCircleIcon className="w-4 h-4" />
												</Popover.Button>
												<Transition
													enter="transition duration-100 ease-out"
													enterFrom="transform opacity-0"
													enterTo="transform opacity-100"
													leave="transition duration-75 ease-out"
													leaveFrom="transform opacity-100"
													leaveTo="transform opacity-0">
													<Popover.Panel className="absolute z-10 left-8 -top-10">
														<div
															className="flex flex-wrap w-64 p-4 border border-gray-300 rounded-md bg-white text-sm text-gray-900 font-normal shadow">
															{translations.detailsForm.vehicleTypeMoreInformation}
														</div>
													</Popover.Panel>
												</Transition>
											</Popover>
										</legend>
										<div className="flex flex-col sm:flex-row sm:justify-between">
											{getVehicleTypeOptions(translations.detailsForm.vehicleTypeTitles).map((option, index) =>
												<div className="flex items-center space-x-2" key={"vehicleType" + option.id + index}>
													<input
														{...register("vehicleType", { required: true })}
														type="radio"
														value={option.id}
														className="w-5 h-5 border-gray-300 text-blue-600 focus:ring-blue-600"
														disabled={option.disabled}
														onClick={() => {
															setValue("vehicleType", option.id, {
																shouldValidate: true,
																shouldTouch: true,
																shouldDirty: true
															})
														}}
														onChange={(e) => {
															setValue("vehicleType", e.target.value);
														}}
													/>
													<label
														className="flex flex-row text-sm font-normal leading-6 text-gray-900">
														{option.title}
														{option.disabled && (
															<Popover className="relative ml-2">
																<Popover.Button>
																	<InformationCircleIcon className="w-4 h-4" />
																</Popover.Button>
																<Transition
																	enter="transition duration-100 ease-out"
																	enterFrom="transform opacity-0"
																	enterTo="transform opacity-100"
																	leave="transition duration-75 ease-out"
																	leaveFrom="transform opacity-100"
																	leaveTo="transform opacity-0">
																	<Popover.Panel className="absolute z-10 left-8 -top-10">
																		<div
																			className="flex flex-wrap w-64 p-4 border border-gray-300 rounded-md bg-white text-sm text-gray-900 font-normal shadow">
																			{translations.detailsForm.vehicleTypeNotInUse}
																		</div>
																	</Popover.Panel>
																</Transition>
															</Popover>
														)}
													</label>
												</div>
											)}
										</div>
										{errors.vehicleType && (
											<div className="mb-3 text-normal text-sm text-red-500">
												{translations.detailsForm.vehicleTypeError}
											</div>
										)}
									</fieldset>
									<fieldset>
										<legend
											className="flex flex-row items-center text-sm font-semibold leading-6 text-gray-900">
											{translations.detailsForm.licencePlateTitle}
											<Popover className="relative ml-2">
												<Popover.Button>
													<InformationCircleIcon className="w-4 h-4" />
												</Popover.Button>
												<Transition
													enter="transition duration-100 ease-out"
													enterFrom="transform opacity-0"
													enterTo="transform opacity-100"
													leave="transition duration-75 ease-out"
													leaveFrom="transform opacity-100"
													leaveTo="transform opacity-0">
													<Popover.Panel className="absolute z-10 left-8 -top-10">
														<div
															className="flex flex-wrap w-64 p-4 border border-gray-300 rounded-md bg-white text-sm text-gray-900 font-normal shadow">
															{translations.detailsForm.licencePlateMoreInformation}
														</div>
													</Popover.Panel>
												</Transition>
											</Popover>
										</legend>
										<div className="flex flex-row">
											<input
												className="border-solid border-gray-300 border border-b-gray-500 py-2 px-4 w-full rounded placeholder-gray-400 focus:outline-none focus:ring-gray-700 focus:border-gray-700 min-h-[46px] uppercase"
												placeholder="ABC-123"
												type="text"
												{...register("plateNumber", {
													required: true,
												})}
											/>
										</div>
										{errors.plateNumber && errors.plateNumber.message &&
											<div className="mb-3 text-normal text-sm text-red-500">
												<span>{translations.detailsForm.licencePlateError}</span>
											</div>
										}
									</fieldset>
									<fieldset>
										<legend
											className="flex flex-row items-center text-sm font-semibold leading-6 text-gray-900">
											{translations.detailsForm.annualMileageTitle}
											<Popover className="relative ml-2">
												<Popover.Button>
													<InformationCircleIcon className="w-4 h-4" />
												</Popover.Button>
												<Transition
													enter="transition duration-100 ease-out"
													enterFrom="transform opacity-0"
													enterTo="transform opacity-100"
													leave="transition duration-75 ease-out"
													leaveFrom="transform opacity-100"
													leaveTo="transform opacity-0">
													<Popover.Panel className="absolute z-10 left-8 -top-10">
														<div
															className="flex flex-wrap w-64 p-4 border border-gray-300 rounded-md bg-white text-sm text-gray-900 font-normal shadow">
															{translations.detailsForm.annualMileageMoreInformation}
														</div>
													</Popover.Panel>
												</Transition>
											</Popover>
										</legend>
										<div className="flex flex-row">
												<Listbox
													{...register("annualMileage", { required: true })}
													value={getValues("annualMileage")}
													onChange={(value: any) => {
														setSelectedAnnualMileage(value)

														setValue("annualMileage", value.id, {
															shouldValidate: true,
															shouldTouch: true,
															shouldDirty: true
														})
													}}
												>
													{({ open }) => (
														<div className="relative w-full">
															<Listbox.Button
																className="relative w-full min-h-[46px] cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-600 sm:text-sm sm:leading-6">
																<span
																	className="block truncate">{selectedAnnualMileage.title}</span>
																<span
																	className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
																	<ChevronDownIcon className="h-4 w-4 text-gray-400" />
																</span>
															</Listbox.Button>
															<Transition
																show={open}
																as={Fragment}
																leave="transition ease-in duration-100"
																leaveFrom="opacity-100"
																leaveTo="opacity-0"
															>
																<Listbox.Options
																	className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
																	{getAnnualMileageOptions(translations.detailsForm.annualMileageTitles).map((option) => (
																		<Listbox.Option
																			key={option.id}
																			disabled={option.disabled}
																			className={({ active }) =>
																				classNames(
																					active ? 'bg-blue-600 text-white' : 'text-gray-900',
																					'relative cursor-pointer select-none py-2 pl-3 pr-9'
																				)
																			}
																			value={option}
																		// onClick={() => {
																		// 	setSelectedAnnualMileage(option)
																		//
																		// 	setValue("annualMileage",  option.id, {
																		// 		shouldValidate: true,
																		// 		shouldTouch: true,
																		// 		shouldDirty: true
																		// 	})
																		// }}
																		>
																			{({ selected, active }) => (
																				<>
																					<span
																						className={classNames(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
																						{option.title}
																					</span>
																					{selected ? (
																						<span
																							className={classNames(
																								active ? 'text-white' : 'text-blue-500',
																								'absolute inset-y-0 right-0 flex items-center pr-4'
																							)}
																						>
																							<CheckIcon className="h-5 w-5" />
																						</span>
																					) : null
																					}
																				</>
																			)}
																		</Listbox.Option>
																	))}
																</Listbox.Options>
																</Transition>
														</div>
													)}
												</Listbox>
										</div>
										{errors.annualMileage && (
											<div className="mb-3 text-normal text-sm text-red-500">
												{translations.detailsForm.annualMileageError}
											</div>
										)}
									</fieldset>
									<fieldset>
										<legend
											className="flex flex-row items-center text-sm font-semibold leading-6 text-gray-900">
											{translations.detailsForm.under25DriverTitle}
											<Popover className="relative ml-2">
												<Popover.Button>
													<InformationCircleIcon className="w-4 h-4" />
												</Popover.Button>
												<Transition
													enter="transition duration-100 ease-out"
													enterFrom="transform opacity-0"
													enterTo="transform opacity-100"
													leave="transition duration-75 ease-out"
													leaveFrom="transform opacity-100"
													leaveTo="transform opacity-0">
													<Popover.Panel className="absolute z-10 left-8 -top-10">
														<div
															className="flex flex-wrap w-64 p-4 border border-gray-300 rounded-md bg-white text-sm text-gray-900 font-normal shadow">
															{translations.detailsForm.under25DriverMoreInformation}
														</div>
													</Popover.Panel>
												</Transition>
											</Popover>
										</legend>
										<div className="flex flex-row space-x-24">
											<div className="flex items-center space-x-2">
												<input
													{...register("under25Driver", { required: true })}
													type="radio"
													value="true"
													className="w-5 h-5 border-gray-300 text-blue-600 focus:ring-blue-600"
													onClick={() => setValue("under25Driver", "true", {
														shouldValidate: true,
														shouldTouch: true,
														shouldDirty: true
													})}
													onChange={(e) => {
														setValue("under25Driver", "true");
													}}
												/>
												<label
													className="block text-sm font-normal leading-6 text-gray-900">{translations.detailsForm.under25DriverTitles.yes}</label>
											</div>
											<div className="flex items-center space-x-2">
												<input
													{...register("under25Driver", { required: true })}
													type="radio"
													value="false"
													className="w-5 h-5 border-gray-300 text-blue-600 focus:ring-blue-600"
													onClick={() => setValue("under25Driver", "false", {
														shouldValidate: true,
														shouldTouch: true,
														shouldDirty: true
													})}
													onChange={(e) => {
														setValue("under25Driver", "false");
													}}
												/>
												<label
													className="block text-sm font-normal leading-6 text-gray-900">{translations.detailsForm.under25DriverTitles.no}</label>
											</div>
										</div>
										{errors.under25Driver && (
											<div className="mb-3 text-normal text-sm text-red-500">
												{translations.detailsForm.under25DriverError}
											</div>
										)}
									</fieldset>
									<fieldset>
										<legend
											className="flex flex-row items-center text-sm font-semibold leading-6 text-gray-900">
											{translations.detailsForm.mainUserTitle}
											<Popover className="relative ml-2">
												<Popover.Button>
													<InformationCircleIcon className="w-4 h-4" />
												</Popover.Button>
												<Transition
													enter="transition duration-100 ease-out"
													enterFrom="transform opacity-0"
													enterTo="transform opacity-100"
													leave="transition duration-75 ease-out"
													leaveFrom="transform opacity-100"
													leaveTo="transform opacity-0">
													<Popover.Panel className="absolute z-10 left-8 -top-10">
														<div
															className="flex flex-wrap w-64 p-4 border border-gray-300 rounded-md bg-white text-sm text-gray-900 font-normal shadow">
															{translations.detailsForm.mainUserMoreInformation}
														</div>
													</Popover.Panel>
												</Transition>
											</Popover>
										</legend>
										<div className="flex flex-row space-x-24">
											<div className="flex items-center space-x-2">
												<input
													{...register("mainUser", { required: true })}
													type="radio"
													value="true"
													className="w-5 h-5 border-gray-300 text-blue-600 focus:ring-blue-600"
													onChange={() => {
														setValue("mainUser", "true", {
															shouldValidate: true,
															shouldTouch: true,
															shouldDirty: true
														})
														setShowMainUserWarning(false)
													}}
												/>
												<label
													className="block text-sm font-normal leading-6 text-gray-900">{translations.detailsForm.mainUserTitles.yes}</label>
											</div>
											<div className="flex items-center space-x-2">
												<input
													{...register("mainUser", { required: true })}
													type="radio"
													value="false"
													className="w-5 h-5 border-gray-300 text-blue-600 focus:ring-blue-600"
													onChange={() => {
														handleShowWarningModal()
														setValue("mainUser", "false", {
															shouldValidate: true,
															shouldTouch: true,
															shouldDirty: true
														})
														setShowMainUserWarning(true)
													}}
												/>
												<label
													className="block text-sm font-normal leading-6 text-gray-900">{translations.detailsForm.mainUserTitles.no}</label>
											</div>
										</div>
										{showMainUserWarning &&
											<div className="mt-2 text-sm font-normal text-gray-500">
												{translations.detailsForm.mainUserUnacceptedAnswer}
											</div>
										}
										{errors.mainUser && (
											<div className="mb-3 text-normal text-sm text-red-500">
												{translations.detailsForm.mainUserError}
											</div>
										)}
									</fieldset>
									<fieldset>
										<legend
											className="flex flex-row items-center text-sm font-semibold leading-6 text-gray-900">
											{translations.detailsForm.carFinancedTitle}
											<Popover className="relative ml-2">
												<Popover.Button>
													<InformationCircleIcon className="w-4 h-4" />
												</Popover.Button>
												<Transition
													enter="transition duration-100 ease-out"
													enterFrom="transform opacity-0"
													enterTo="transform opacity-100"
													leave="transition duration-75 ease-out"
													leaveFrom="transform opacity-100"
													leaveTo="transform opacity-0">
													<Popover.Panel className="absolute z-10 left-8 -top-10">
														<div
															className="flex flex-wrap w-64 p-4 border border-gray-300 rounded-md bg-white text-sm text-gray-900 font-normal shadow">
															{translations.detailsForm.carFinancedMoreInformation}
														</div>
													</Popover.Panel>
												</Transition>
											</Popover>
										</legend>
										<div className="flex flex-row space-x-24">
											<div className="flex items-center space-x-2">
												<input
													{...register("carFinanced", { required: true })}
													type="radio"
													value="true"
													className="w-5 h-5 border-gray-300 text-blue-600 focus:ring-blue-600"
													onClick={() => setValue("carFinanced", "true", {
														shouldValidate: true,
														shouldTouch: true,
														shouldDirty: true
													})}
													onChange={(e) => {
														setValue("carFinanced", "true");
													}}
												/>
												<label
													className="block text-sm font-normal leading-6 text-gray-900">{translations.detailsForm.carFinancedTitles.yes}</label>
											</div>
											<div className="flex items-center space-x-2">
												<input
													{...register("carFinanced", { required: true })}
													type="radio"
													value="false"
													className="w-5 h-5 border-gray-300 text-blue-600 focus:ring-blue-600"
													onClick={() => setValue("carFinanced", "false", {
														shouldValidate: true,
														shouldTouch: true,
														shouldDirty: true
													})}
													onChange={(e) => {
														setValue("carFinanced", "false");
													}}
												/>
												<label
													className="block text-sm font-normal leading-6 text-gray-900">{translations.detailsForm.carFinancedTitles.no}</label>
											</div>
										</div>
										{errors.carFinanced && (
											<div className="mb-3 text-normal text-sm text-red-500">
												{translations.detailsForm.carFinancedError}
											</div>
										)}
									</fieldset>
									<fieldset>
										<legend
											className="flex flex-row items-center text-sm font-semibold leading-6 text-gray-900">
											{translations.detailsForm.professionalUseTitle}
											<Popover className="relative ml-2">
												<Popover.Button>
													<InformationCircleIcon className="w-4 h-4" />
												</Popover.Button>
												<Transition
													enter="transition duration-100 ease-out"
													enterFrom="transform opacity-0"
													enterTo="transform opacity-100"
													leave="transition duration-75 ease-out"
													leaveFrom="transform opacity-100"
													leaveTo="transform opacity-0">
													<Popover.Panel className="absolute z-10 left-8 -top-10">
														<div
															className="flex flex-wrap w-64 p-4 border border-gray-300 rounded-md bg-white text-sm text-gray-900 font-normal shadow">
															{translations.detailsForm.professionalUseMoreInformation}
														</div>
													</Popover.Panel>
												</Transition>
											</Popover>
										</legend>
										<div className="flex flex-row space-x-24">
											<div className="flex items-center space-x-2">
												<input
													{...register("professionalUse", { required: true })}
													type="radio"
													value="true"
													className="w-5 h-5 border-gray-300 text-blue-600 focus:ring-blue-600"
													onChange={() => {
														handleShowWarningModal()
														setValue("professionalUse", "true", {
															shouldValidate: true,
															shouldTouch: true,
															shouldDirty: true
														})
														setShowProfessionalUseWarning(true)
													}}
												/>
												<label
													className="block text-sm font-normal leading-6 text-gray-900">{translations.detailsForm.professionalUseTitles.yes}</label>
											</div>
											<div className="flex items-center space-x-2">
												<input
													{...register("professionalUse", { required: true })}
													type="radio"
													value="false"
													className="w-5 h-5 border-gray-300 text-blue-600 focus:ring-blue-600"
													onChange={() => {
														setValue("professionalUse", "false", {
															shouldValidate: true,
															shouldTouch: true,
															shouldDirty: true
														})
														setShowProfessionalUseWarning(false)
													}}
												/>
												<label
													className="block text-sm font-normal leading-6 text-gray-900">{translations.detailsForm.professionalUseTitles.no}</label>
											</div>
										</div>
										{showProfessionalUseWarning &&
											<div className="mt-2 text-sm font-normal text-gray-500">
												{translations.detailsForm.professionalUseUnacceptedAnswer}
											</div>
										}
										{errors.professionalUse && (
											<div className="mb-3 text-normal text-sm text-red-500">
												{translations.detailsForm.professionalUseError}
											</div>
										)}
									</fieldset>
								</div>
							</div>
						</div>

						<div className="flex flex-row w-full border-b my-10"></div>

						<div className="flex flex-col w-full sm:mb-4">
							<h3
								className="mb-2 pb-2 text-2xl font-medium text-gray-700">{translations.detailsForm.section2Header}</h3>
							<div className="flex flex-row flex-wrap">
								<div className="basis-full sm:basis-1/1 sm:pr-4 py-4 space-y-6">
									<fieldset>
										<legend
											className="flex flex-row items-center text-sm font-semibold leading-6 text-gray-900">
											{translations.detailsForm.socialSecurityNumberTitle} <span
												className="ml-1 text-red-700">*</span>
										</legend>
										<div className="flex flex-row">
											<input
												className="border-solid border-gray-300 border border-b-gray-500 py-2 px-4 w-full rounded placeholder-gray-400 focus:outline-none focus:ring-gray-700 focus:border-gray-700 min-h-[46px] uppercase"
												placeholder={translations.detailsForm.socialSecurityNumberPlaceholder}
												type="text"
												{...register("socialSecurityNumber", {
													required: true,
													disabled: false,
													maxLength: 11
												})}
											/>
										</div>
										<div className="mt-2 text-sm font-normal text-gray-500">
											{translations.detailsForm.socialSecurityNumberDescription}
										</div>
										{errors.socialSecurityNumber && (
											<div className="mb-3 text-normal text-sm text-red-500">
												{translations.detailsForm.socialSecurityNumberError}
											</div>
										)}
									</fieldset>
									<fieldset>
										<legend
											className="flex flex-row items-center text-sm font-semibold leading-6 text-gray-900">
											{translations.detailsForm.postalNumberTitle}
											<Popover className="relative ml-2">
												<Popover.Button>
													<InformationCircleIcon className="w-4 h-4" />
												</Popover.Button>
												<Transition
													enter="transition duration-100 ease-out"
													enterFrom="transform opacity-0"
													enterTo="transform opacity-100"
													leave="transition duration-75 ease-out"
													leaveFrom="transform opacity-100"
													leaveTo="transform opacity-0">
													<Popover.Panel className="absolute z-10 left-8 -top-10">
														<div
															className="flex flex-wrap w-64 p-4 border border-gray-300 rounded-md bg-white text-sm text-gray-900 font-normal shadow">
															{translations.detailsForm.postalNumberMoreInformation}
														</div>
													</Popover.Panel>
												</Transition>
											</Popover>
										</legend>
										<div className="flex flex-row">
											<input
												className="border-solid border-gray-300 border border-b-gray-500 py-2 px-4 w-full rounded placeholder-gray-400 focus:outline-none focus:ring-gray-700 focus:border-gray-700 min-h-[46px]"
												placeholder={translations.detailsForm.postalNumberPlaceholder}
												type="text"
												{...register("postalNumber", {
													required: true,
													disabled: false,
												})}
											/>
										</div>
										{errors.postalNumber && (
											<div className="mb-3 text-normal text-sm text-red-500">
												{translations.detailsForm.postalNumberError}
											</div>
										)}
									</fieldset>
								</div>
							</div>
						</div>

						<div className="flex flex-row w-full border-b my-4"></div>
						<div className="flex flex-row items-center justify-end w-full mb-4">
							{getFieldState("mainUser")?.isDirty && getFieldState("professionalUse")?.isDirty && !formIsValid() &&
								<div
									className="flex flex-row items-center mr-4 space-x-2 text-sm font-normal text-red-500">
									<InformationCircleIcon className="w-5 h-5" />
									<span>{translations.detailsForm.generalErrorInForm}</span>
								</div>
							}
							<button
								className={classNames(
									formIsValid() ? primaryCtaClass : "bg-gray-300 text-gray-500",
									"flex flex-row items-center my-2 duration-200 border py-3 px-8 font-semibold text-md rounded-md transition-all"
								)}
								type="submit"
								disabled={!formIsValid() || isLoading}
								onClick={() => {
									sendEvent("", hideHeader, {
										category: 'seeResults',
										action: 'click',
										label: "seeResults",
										gclid: searchParams.get("gclid") ?? ""
									})
								}}
							>
								<ChevronRightIcon className="mr-2 h-4 w-4 text-gray-white" />
								{translations.detailsForm.submitButtonTitle}
							</button>
						</div>
					</form>
					<DevTool control={control} />
					<NotificationModal
						open={open}
						setOpen={setOpen}
						cancelButtonRef={cancelButtonRef}
						title={translations.detailsForm.warningModalTitle}
						description={translations.detailsForm.warningModalDescription}
						buttonTitle={translations.detailsForm.warningModalButtonTitle}
					/>
				</div>
			</div>
		</div>
	)
}

export default DetailsForm
